import { useEffect, useState } from "react";
import { LogOut, Save } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { CalendarConnectionPanel } from "@/components/CalendarConnectionPanel";
import { DeleteAccountSection } from "@/components/DeleteAccountSection";
import { PremiumCard } from "@/components/PremiumCard";
import { MacroPresetPanel } from "@/components/MacroPresetPanel";
import { MobileShell } from "@/components/MobileShell";
import { ShareHistoryPanel } from "@/components/ShareHistoryPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { api, getApiError } from "@/lib/api";
import { findActiveMacroPreset } from "@/lib/macros";


export default function ProfilePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, refreshUser, logout } = useAuth();
  const [retailers, setRetailers] = useState([]);
  const [macroPresets, setMacroPresets] = useState([]);
  const [history, setHistory] = useState({ created_shares: [], templates: [], recent_activity: [] });
  const [applyingTemplateId, setApplyingTemplateId] = useState(null);
  const [macroBusy, setMacroBusy] = useState(false);
  const [saveCurrentName, setSaveCurrentName] = useState("");
  const [manualPreset, setManualPreset] = useState({
    name: "",
    calorie_target_daily: 2000,
    daily_carb_target: 180,
    max_carbs_per_meal: "",
    carb_priority_mode: "balanced",
  });
  const [form, setForm] = useState({
    name: "",
    language: "nl",
    household_size: 2,
    calorie_target_daily: 2100,
    daily_carb_target: 180,
    max_carbs_per_meal: "",
    carb_priority_mode: "balanced",
    preferred_retailer_id: "",
  });
  const [calendarConnection, setCalendarConnection] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarBusy, setCalendarBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [calendarEventDraft, setCalendarEventDraft] = useState({
    title: "",
    start_at: "",
    end_at: "",
    all_day: false,
    provider: "google_calendar",
  });
  const activeMacroPreset = findActiveMacroPreset(macroPresets, form);

  useEffect(() => {
    const fetchProfileMeta = async () => {
      const [retailersResponse, historyResponse, presetsResponse, calendarConnectionResponse, calendarEventsResponse] = await Promise.all([
        api.get("/retailers"),
        api.get("/planner/shares/history"),
        api.get("/profile/macro-presets"),
        api.get("/planner/calendar-connection"),
        api.get("/planner/calendar/events"),
      ]);
      setRetailers(retailersResponse.data);
      setHistory(historyResponse.data);
      setMacroPresets(presetsResponse.data);
      setCalendarConnection(calendarConnectionResponse.data);
      setCalendarEvents(calendarEventsResponse.data.events || []);
    };

    fetchProfileMeta();
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }

    setForm({
      name: user.name,
      language: user.language,
      household_size: user.household_size,
      calorie_target_daily: user.calorie_target_daily,
      daily_carb_target: user.daily_carb_target,
      max_carbs_per_meal: user.max_carbs_per_meal || "",
      carb_priority_mode: user.carb_priority_mode || "balanced",
      preferred_retailer_id: user.preferred_retailer_id || "",
    });
    setManualPreset({
      name: "",
      calorie_target_daily: user.calorie_target_daily,
      daily_carb_target: user.daily_carb_target,
      max_carbs_per_meal: user.max_carbs_per_meal || "",
      carb_priority_mode: user.carb_priority_mode || "balanced",
    });
  }, [user]);

  useEffect(() => {
    const oauthStatus = searchParams.get("calendar_oauth");
    if (!oauthStatus) {
      return;
    }
    if (oauthStatus === "connected") {
      toast.success("Google Calendar verbonden en eerste sync uitgevoerd.");
    } else if (oauthStatus === "failed") {
      toast.error("Google Calendar koppeling of sync mislukte.");
    }
  }, [searchParams]);

  useEffect(() => {
    const billingStatus = searchParams.get("billing");
    if (!billingStatus) {
      return;
    }
    if (billingStatus === "success") {
      refreshUser();
      toast.success("Bedankt! Je Smartly Premium-abonnement wordt geactiveerd zodra de betaling is bevestigd.");
    } else if (billingStatus === "cancelled") {
      toast("Checkout geannuleerd — je gebruikt Smartly gewoon gratis verder.");
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("billing");
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams, refreshUser]);

  const applyMacroPreset = (preset) => {
    setForm((current) => ({
      ...current,
      calorie_target_daily: preset.calorie_target_daily,
      daily_carb_target: preset.daily_carb_target,
      max_carbs_per_meal: preset.max_carbs_per_meal || "",
      carb_priority_mode: preset.carb_priority_mode,
    }));
    toast.success(`${preset.name} geladen. Tik op Opslaan om dit profiel actief te maken.`);
  };

  const saveCurrentAsMacroPreset = async () => {
    if (!saveCurrentName.trim()) {
      return;
    }

    try {
      setMacroBusy(true);
      await api.post("/profile/macro-presets", {
        name: saveCurrentName.trim(),
        calorie_target_daily: Number(form.calorie_target_daily),
        daily_carb_target: Number(form.daily_carb_target),
        max_carbs_per_meal: form.max_carbs_per_meal ? Number(form.max_carbs_per_meal) : null,
        carb_priority_mode: form.carb_priority_mode,
      });
      setSaveCurrentName("");
      const presetsResponse = await api.get("/profile/macro-presets");
      setMacroPresets(presetsResponse.data);
      toast.success("Huidige macro preset opgeslagen.");
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setMacroBusy(false);
    }
  };

  const createManualMacroPreset = async () => {
    if (!manualPreset.name.trim()) {
      return;
    }

    try {
      setMacroBusy(true);
      await api.post("/profile/macro-presets", {
        name: manualPreset.name.trim(),
        calorie_target_daily: Number(manualPreset.calorie_target_daily),
        daily_carb_target: Number(manualPreset.daily_carb_target),
        max_carbs_per_meal: manualPreset.max_carbs_per_meal ? Number(manualPreset.max_carbs_per_meal) : null,
        carb_priority_mode: manualPreset.carb_priority_mode,
      });
      setManualPreset((current) => ({ ...current, name: "" }));
      const presetsResponse = await api.get("/profile/macro-presets");
      setMacroPresets(presetsResponse.data);
      toast.success("Custom macro preset opgeslagen.");
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setMacroBusy(false);
    }
  };

  const saveProfile = async () => {
    if (saving) {
      return;
    }
    try {
      setSaving(true);
      const trimmedName = (form.name || "").trim();
      if (!trimmedName) {
        toast.error("Naam mag niet leeg zijn.");
        return;
      }
      const householdSize = Number(form.household_size);
      const calorieTarget = Number(form.calorie_target_daily);
      const carbTarget = Number(form.daily_carb_target);
      if (!Number.isFinite(householdSize) || householdSize < 1 || householdSize > 8) {
        toast.error("Huishouden moet tussen 1 en 8 zijn.");
        return;
      }
      if (!Number.isFinite(calorieTarget) || calorieTarget < 1200 || calorieTarget > 4500) {
        toast.error("Calorie-doel moet tussen 1200 en 4500 zijn.");
        return;
      }
      if (!Number.isFinite(carbTarget) || carbTarget < 40 || carbTarget > 400) {
        toast.error("Carb-doel moet tussen 40 en 400 zijn.");
        return;
      }
      const payload = {
        name: trimmedName,
        language: form.language || "nl",
        household_size: householdSize,
        calorie_target_daily: calorieTarget,
        daily_carb_target: carbTarget,
        max_carbs_per_meal: form.max_carbs_per_meal === "" || form.max_carbs_per_meal === null || form.max_carbs_per_meal === undefined
          ? null
          : Number(form.max_carbs_per_meal),
        carb_priority_mode: form.carb_priority_mode || "balanced",
        preferred_retailer_id: form.preferred_retailer_id || null,
      };
      await api.put("/profile", payload);
      await refreshUser();
      toast.success("Profiel opgeslagen.");
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const logoutUser = () => {
    logout();
    navigate("/login");
  };

  const refreshCalendarMeta = async () => {
    const [calendarConnectionResponse, calendarEventsResponse] = await Promise.all([
      api.get("/planner/calendar-connection"),
      api.get("/planner/calendar/events"),
    ]);
    setCalendarConnection(calendarConnectionResponse.data);
    setCalendarEvents(calendarEventsResponse.data.events || []);
  };

  const updateCalendarDraft = (field, value) => {
    setCalendarEventDraft((current) => ({ ...current, [field]: value }));
  };

  const selectCalendarProvider = async (provider) => {
    try {
      setCalendarBusy(true);
      const response = await api.post("/planner/calendar-connection", { provider });
      setCalendarConnection(response.data);
      toast.success(provider === "google_calendar" ? "Google Calendar staat klaar als real sync foundation." : "Apple Calendar blijft een seam/placeholder.");
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setCalendarBusy(false);
    }
  };

  const disconnectCalendar = async () => {
    try {
      setCalendarBusy(true);
      const response = await api.delete("/planner/calendar-connection");
      setCalendarConnection(response.data);
      toast.success("Kalenderkoppeling uitgeschakeld.");
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setCalendarBusy(false);
    }
  };

  const syncGoogleCalendar = async () => {
    try {
      setCalendarBusy(true);
      const response = await api.post("/planner/calendar/google/sync");
      await refreshCalendarMeta();
      toast.success(response.data.message || "Google Calendar sync gecontroleerd.");
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setCalendarBusy(false);
    }
  };

  const connectGoogleCalendar = async () => {
    try {
      setCalendarBusy(true);
      const response = await api.get("/planner/calendar/google/oauth-url");
      if (response.data.authorization_url) {
        window.location.assign(response.data.authorization_url);
        return;
      }
      toast.error(response.data.message || "Google Calendar staat nog niet klaar om te verbinden.");
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setCalendarBusy(false);
    }
  };

  const importCalendarEvent = async () => {
    if (!calendarEventDraft.title.trim() || !calendarEventDraft.start_at || !calendarEventDraft.end_at) {
      toast.error("Vul titel, start en eind in.");
      return;
    }

    try {
      setCalendarBusy(true);
      await api.post("/planner/calendar/events", {
        title: calendarEventDraft.title.trim(),
        start_at: new Date(calendarEventDraft.start_at).toISOString(),
        end_at: new Date(calendarEventDraft.end_at).toISOString(),
        all_day: calendarEventDraft.all_day,
        provider: calendarEventDraft.provider,
      });
      setCalendarEventDraft((current) => ({ ...current, title: "", start_at: "", end_at: "" }));
      await refreshCalendarMeta();
      toast.success("Kalenderevent geïmporteerd.");
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setCalendarBusy(false);
    }
  };

  const applyTemplate = async (templateId) => {
    try {
      setApplyingTemplateId(templateId);
      const response = await api.post(`/planner/templates/${templateId}/apply`, {});
      toast.success("Template in planner gezet.");
      navigate(`/planner?week_start=${response.data.week_start}`);
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setApplyingTemplateId(null);
    }
  };

  return (
    <MobileShell title="Profiel" eyebrow="Persoonlijke plannerinstellingen" action={<Button className="rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:cursor-not-allowed disabled:opacity-70" onClick={saveProfile} disabled={saving} data-testid="profile-save-button"><Save className="mr-2 h-4 w-4" />{saving ? "Opslaan..." : "Opslaan"}</Button>}>
      <div className="space-y-4" data-testid="profile-page">
        <section className="rounded-[28px] border border-stone-200 bg-white/90 p-4" data-testid="profile-identity-card">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-stone-500" data-testid="profile-email-kicker">
            Account
          </p>
          <h2 className="mt-2 text-2xl font-semibold text-stone-900" data-testid="profile-name-title">
            {user?.name}
          </h2>
          <p className="mt-2 text-sm text-stone-600" data-testid="profile-email-value">
            {user?.email}
          </p>
        </section>

        <section className="space-y-4 rounded-[28px] border border-stone-200 bg-white/90 p-4" data-testid="profile-form-card">
          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" htmlFor="profile-name">
              Naam
            </label>
            <Input id="profile-name" name="name" autoComplete="name" value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} className="h-12 rounded-2xl border-stone-200" data-testid="profile-name-input" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">
                Taal
              </label>
              <Select value={form.language} onValueChange={(value) => setForm((current) => ({ ...current, language: value }))}>
                <SelectTrigger className="h-12 rounded-2xl border-stone-200" data-testid="profile-language-select">
                  <SelectValue placeholder="Taal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nl">Nederlands</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" htmlFor="profile-household">
                Huishouden
              </label>
              <Input id="profile-household" name="household_size" type="number" inputMode="numeric" min="1" max="8" value={form.household_size} onChange={(event) => setForm((current) => ({ ...current, household_size: event.target.value }))} className="h-12 rounded-2xl border-stone-200" data-testid="profile-household-input" />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" htmlFor="profile-calories">
              Dagelijks calorie-doel
            </label>
            <Input id="profile-calories" name="calorie_target_daily" type="number" inputMode="numeric" min="1200" max="4500" value={form.calorie_target_daily} onChange={(event) => setForm((current) => ({ ...current, calorie_target_daily: event.target.value }))} className="h-12 rounded-2xl border-stone-200" data-testid="profile-calorie-input" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" htmlFor="profile-carbs">
                Dagelijks carb-doel
              </label>
              <Input id="profile-carbs" name="daily_carb_target" type="number" inputMode="numeric" min="40" max="400" value={form.daily_carb_target} onChange={(event) => setForm((current) => ({ ...current, daily_carb_target: event.target.value }))} className="h-12 rounded-2xl border-stone-200" data-testid="profile-carb-input" />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" htmlFor="profile-max-carbs">
                Max carbs per meal
              </label>
              <Input id="profile-max-carbs" name="max_carbs_per_meal" type="number" inputMode="numeric" min="10" max="200" value={form.max_carbs_per_meal} onChange={(event) => setForm((current) => ({ ...current, max_carbs_per_meal: event.target.value }))} placeholder="Optioneel" className="h-12 rounded-2xl border-stone-200" data-testid="profile-max-carb-input" />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">
              Carb-priority
            </label>
            <Select value={form.carb_priority_mode} onValueChange={(value) => setForm((current) => ({ ...current, carb_priority_mode: value }))}>
              <SelectTrigger className="h-12 rounded-2xl border-stone-200" data-testid="profile-carb-priority-select">
                <SelectValue placeholder="Kies carb-priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low_carb">Low carb</SelectItem>
                <SelectItem value="balanced">Balanced</SelectItem>
                <SelectItem value="flexible">Flexible</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">
              Voorkeursretailer
            </label>
            <Select value={form.preferred_retailer_id} onValueChange={(value) => setForm((current) => ({ ...current, preferred_retailer_id: value }))}>
              <SelectTrigger className="h-12 rounded-2xl border-stone-200" data-testid="profile-retailer-select">
                <SelectValue placeholder="Kies retailer" />
              </SelectTrigger>
              <SelectContent>
                {retailers.map((retailer) => (
                  <SelectItem key={retailer.id} value={retailer.id}>
                    {retailer.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </section>

        <CalendarConnectionPanel
          connection={calendarConnection}
          events={calendarEvents}
          eventDraft={calendarEventDraft}
          busy={calendarBusy}
          onDraftChange={updateCalendarDraft}
          onSelectProvider={selectCalendarProvider}
          onConnectGoogle={connectGoogleCalendar}
          onSyncGoogle={syncGoogleCalendar}
          onDisconnect={disconnectCalendar}
          onImportEvent={importCalendarEvent}
        />

        <MacroPresetPanel
          presets={macroPresets}
          activePresetId={activeMacroPreset?.id}
          activePresetLabel={activeMacroPreset?.name}
          onApplyPreset={applyMacroPreset}
          saveCurrentName={saveCurrentName}
          onSaveCurrentNameChange={setSaveCurrentName}
          onSaveCurrentPreset={saveCurrentAsMacroPreset}
          manualPreset={manualPreset}
          onManualPresetChange={(field, value) => setManualPreset((current) => ({ ...current, [field]: value }))}
          onCreateManualPreset={createManualMacroPreset}
          busy={macroBusy}
        />

        <ShareHistoryPanel history={history} onApplyTemplate={applyTemplate} applyingTemplateId={applyingTemplateId} />

        <PremiumCard />

        <Button variant="outline" className="h-12 w-full rounded-full border-stone-200 bg-white" onClick={logoutUser} data-testid="profile-logout-button">
          <LogOut className="mr-2 h-4 w-4" />
          Uitloggen
        </Button>

        <DeleteAccountSection />
      </div>
    </MobileShell>
  );
}